import { AssignmentVisualizationsPanel } from "@app/components/assignment-visualizations-panel.tsx";
import AssignmentBillingForm from "@app/components/forms/assignments/assignment-billing-form.tsx";
import AssignmentBudgetForm from "@app/components/forms/assignments/assignment-budget-form.tsx";
import AssignmentGeneralForm from "@app/components/forms/assignments/assignment-general-form.tsx";
import AssignmentPhasesForm from "@app/components/forms/assignments/assignment-phases-form.tsx";
import AssignmentRatesDefaultsForm from "@app/components/forms/assignments/assignment-rates-defaults-form.tsx";
import AssignmentRatesForm from "@app/components/forms/assignments/assignment-rates-form.tsx";
import AssignmentRosterForm from "@app/components/forms/assignments/assignment-roster-form.tsx";
import AssignmentWorkLocationsForm from "@app/components/forms/assignments/assignment-work-locations-form.tsx";
import AssignmentWorkPackagesForm from "@app/components/forms/assignments/assignment-work-packages-form.tsx";
import Kvp from "@app/components/kvp.tsx";
import PageTitle from "@app/components/page-title.tsx";
import useAssignmentData from "@app/data-hooks/use-assignment-data.ts";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Tab } from "@mui/material";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { QueryStatusRouter } from "bi-lib";
import { useState } from "react";

interface SearchParams {
    tab1?: string;
    tab2?: string;
}

export const Route = createFileRoute("/_authenticated/_manager/assignment/$assignmentId")({
    component: Component,
    validateSearch: (search: Record<string, unknown>): SearchParams => {
        return {
            tab1: search.tab1 as string | undefined,
            tab2: search.tab2 as string | undefined,
        };
    },
});

function Component() {
    const search = Route.useSearch();
    const [activeTab, setActiveTab] = useState(search.tab1 ?? "general");

    const assignmentId = +Route.useParams().assignmentId;

    const { completeAssignment } = useAssignmentData({ id: assignmentId });
    const navigate = useNavigate();

    const handleChangeTab = (tab: string) => {
        setActiveTab(tab);
        void navigate({ to: ".", search: { ...search, tab1: tab } });
    };

    return (
        <>
            <PageTitle>Assignment Management</PageTitle>
            <QueryStatusRouter queryHook={completeAssignment}>
                <Box py={1}>
                    <Kvp keyTitle={"Client"} keyWidth={3}>
                        {completeAssignment.data?.clientName}
                    </Kvp>
                    <Kvp keyTitle={"Assignment"} keyWidth={3}>
                        {completeAssignment.data?.displayName}
                    </Kvp>
                </Box>
                <TabContext value={activeTab}>
                    <TabList onChange={(_, value) => handleChangeTab(value)}>
                        <Tab value={"general"} label={"General"} />
                        <Tab value={"billing"} label={"Billing"} />
                        <Tab value={"roster"} label={"Roster"} />
                        <Tab value={"rates"} label={"Rates"} />
                        <Tab value={"detailDefaults"} label={"Detail Defaults"} />
                        <Tab value={"phases"} label={"Phases"} />
                        <Tab value={"workPackages"} label={"Work Packages"} />
                        <Tab value={"workLocations"} label={"Work Locations"} />
                        <Tab value={"budget"} label={"Budget"} />
                        <Tab value={"visualizations"} label={"Visualizations"} />
                    </TabList>
                    <TabPanel value={"general"}>
                        <AssignmentGeneralForm id={assignmentId} />
                    </TabPanel>
                    <TabPanel value={"billing"}>
                        <AssignmentBillingForm id={assignmentId} />
                    </TabPanel>
                    <TabPanel value={"roster"}>
                        <AssignmentRosterForm id={assignmentId} />
                    </TabPanel>
                    <TabPanel value={"rates"}>
                        <AssignmentRatesForm id={assignmentId} />
                    </TabPanel>
                    <TabPanel value={"detailDefaults"}>
                        <AssignmentRatesDefaultsForm id={assignmentId} />
                    </TabPanel>
                    <TabPanel value={"phases"}>
                        <AssignmentPhasesForm id={assignmentId} />
                    </TabPanel>
                    <TabPanel value={"workPackages"}>
                        <AssignmentWorkPackagesForm id={assignmentId} />
                    </TabPanel>
                    <TabPanel value={"workLocations"}>
                        <AssignmentWorkLocationsForm id={assignmentId} />
                    </TabPanel>
                    <TabPanel value={"budget"}>
                        <AssignmentBudgetForm id={assignmentId} />
                    </TabPanel>
                    <TabPanel value={"visualizations"}>
                        <AssignmentVisualizationsPanel assignmentId={assignmentId} />
                    </TabPanel>
                </TabContext>
            </QueryStatusRouter>
        </>
    );
}
